import React from 'react';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { useTranslation } from '../../../../../hooks/useTranslation';
import Link from '../../../../text-link';
import colors from '../../../../../themes/main-colors';
import clsx from 'clsx';

import {
  OutageLookupResponse,
  PremiseInfo,
} from '../../../../../__generated__/pge-types';
import useUtils from '../../utils';
import { NotificationRequestAccountParams } from '../../../OutageCenterPage';

const useStyles = makeStyles(() =>
  createStyles({
    knownOutage: {
      color: colors.errorRed,
    },
    accountName: {
      '& a': {
        wordBreak: 'break-all',
        paddingRight: '5px',
      },
      '& span': {
        display: 'inline-block',
      },
    },
  }),
);

interface Props {
  accountIdentifier: string;
  hasKnownOutage: boolean;
  isPrimary: boolean;
  addressLine1: string;
  accountNumber: string;
  premiseInfos: PremiseInfo[];
  setSinglePremise: (
    data: OutageLookupResponse,
    accountNumber?: string,
    accountIdentifier?: string,
  ) => void;
  setMultiPremise: (
    data: OutageLookupResponse,
    accountNumber?: string,
    accountIdentifier?: string,
  ) => void;
  encryptedPersonId: string;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
  setSelectedEncryptedPremiseId?: (
    encryptedPremiseId: string | null | undefined,
  ) => void;
}

export const UserDetailsBlock = (props: Props) => {
  const { buildLeftPanelDataFromAccountPremise } = useUtils();
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    accountIdentifier,
    hasKnownOutage,
    isPrimary,
    addressLine1,
    setSinglePremise,
    setMultiPremise,
    premiseInfos,
    accountNumber,
    encryptedPersonId,
    setLoggedInUserNotificationRequestAccountParams,
    setSelectedEncryptedPremiseId,
  } = props;
  const tag = hasKnownOutage
    ? ` (${t('OUTAGE_REPORTED')})`
    : isPrimary
    ? ` (${t('PRIMARY')})`
    : '';

  const handleOnClick = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    selectedAccountParams: NotificationRequestAccountParams,
  ) => {
    e.preventDefault();
    if (premiseInfos) {
      const accountName = `${accountIdentifier}${
        isPrimary ? ` (${t('PRIMARY')})` : ''
      }`;
      if (premiseInfos.length === 1) {
        const premise = premiseInfos[0];
        if (premise?.servicePointDetails) {
          const data = buildLeftPanelDataFromAccountPremise(premise);
          data &&
            setSinglePremise &&
            setSinglePremise(data, accountNumber, accountName);

          data &&
            setSelectedEncryptedPremiseId &&
            setSelectedEncryptedPremiseId(data?.encryptedPremiseId);
        }
      } else {
        const data: OutageLookupResponse = {
          isAccountExists: true,
          isEligibleToReport: true, // TODO: Confirm with Ishrat
          multiplePremiseList: premiseInfos?.map(singlePremise => ({
            encryptedValue: singlePremise.encryptedPremiseId,
            houseNumber: `${singlePremise.addressLine1},
            ${singlePremise.city}, ${singlePremise.state} ${singlePremise.postal}`,
            last4CharAddress: '', //To avoid undefined value in the Multiple Premise popup
            premiseInfo: singlePremise,
          })),
        };
        setMultiPremise(data, accountNumber, accountName);
      }
      setLoggedInUserNotificationRequestAccountParams(selectedAccountParams);
    } else {
      //TODO: Need to handle the Premise info null case here
    }
  };

  return (
    <>
      <Typography className={classes.accountName} variant="subtitle2">
        <Link
          to={'/'}
          plain
          onClick={e => handleOnClick(e, { accountNumber, encryptedPersonId })}
          dataAttributes={{
            'data-pageelement': isPrimary
              ? 'primary-account'
              : 'secondary-account',
          }}
        >
          {accountNumber}{' '}
          {accountIdentifier !== accountNumber && `- ${accountIdentifier}`}
        </Link>
        <span className={clsx(hasKnownOutage && classes.knownOutage)}>
          {tag}
        </span>
      </Typography>
      <Typography variant="body2">{addressLine1}</Typography>
    </>
  );
};

export default UserDetailsBlock;
